import React, { useMemo } from 'react';
import type { EmployeeReport } from '../data/types';
import { getReportData } from '../lib/store';
import { computeEmployeeDetail } from '../lib/employeeDetail';
import {
  pct,
  pctColor,
  WeekAvailability,
  EntriesTable,
  SummaryStat,
  DailyHoursChart,
  WeeklyTrendChart,
  ClientHoursCharts,
} from './EmployeeDetailSections';

interface Props {
  employee: EmployeeReport | null;
  period: { startDate: string; endDate: string; label?: string };
  onClose: () => void;
}

export function EmployeeDetailModal({ employee, period, onClose }: Props) {
  const detail = useMemo(
    () =>
      employee
        ? computeEmployeeDetail(employee.employeeId, getReportData(), period)
        : null,
    [employee, period]
  );

  if (!employee || !detail) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-fycom-navy text-white px-6 py-4 flex items-center justify-between sticky top-0">
          <div>
            <h3 className="text-lg font-bold">{employee.displayName}</h3>
            <p className="text-slate-300 text-xs">
              {[employee.area, employee.company, employee.role]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-300 hover:text-white text-2xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Resumen rápido del período actual */}
          <div className="grid grid-cols-2 gap-4">
            <SummaryStat
              label="% Cumpl Total"
              value={pct(employee.pctTotal)}
              color={pctColor(employee.pctTotal)}
              sub={`${employee.actualTotalHours.toFixed(1)} / ${employee.expectedTotalHours.toFixed(1)} hrs`}
            />
            <SummaryStat
              label="% Cumpl Facturable"
              value={pct(employee.pctBillable)}
              color={pctColor(employee.pctBillable)}
              sub={`${employee.actualBillableHours.toFixed(1)} / ${employee.expectedBillableHours.toFixed(1)} hrs`}
            />
          </div>

          {/* Disponibilidad semanal (solo si el período es una semana) */}
          {detail.weekAvailability.length > 0 && (
            <WeekAvailability days={detail.weekAvailability} />
          )}

          {!detail.hasData ? (
            <div className="text-center text-slate-500 py-8">
              Este colaborador no tiene horas registradas en el período cargado.
            </div>
          ) : (
            <>
              {/* Detalle de registros del período seleccionado */}
              <EntriesTable entries={detail.periodEntries} periodLabel={period.label} />

              {/* Distribución de horas por cliente (Top 3 + Otros) */}
              <ClientHoursCharts entries={detail.periodEntries} />

              {/* #2 — Horas por día (últimos 30 días) */}
              <DailyHoursChart data={detail.dailyHours} />

              {/* #3 — Tendencia semanal del % facturable */}
              <WeeklyTrendChart data={detail.weeklyTrend} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
